import React, { useState, useContext } from 'react';
import {FormattedMessage, useIntl, IntlContext} from 'react-intl';
import axios from 'axios';
import BarLoader from "react-spinners/BarLoader";
import AppContext from '../components/AppContext';
import { Auth } from 'aws-amplify';
import { currentLongLang, bytesToNearestUnitStr } from './components/utils';
import removeAccents from 'remove-accents';

//TODO: this is being created for every single item in the table, doesn't seem efficient...

export default function FileUpload(props) {
	const appContext = useContext(AppContext);
	const { messages } = useContext(IntlContext);
	
	const defaultMaxFilesPerSubmission = 300;
	const defaultMaxConcurrentUploadRequests = 2;
	const defaultDailyMax = 25;	//must match "FileUploadLimit" inside /template.yml or else the error message will be inconsistent with behaviour
	//const defaultDailyDataMax = 50000000000;	//50GB
	const defaultMaxVolumePerSubmission = 4000000000; //4GB

	const labels = {
		"error.tooManyFiles"                  : useIntl().formatMessage({id:"error.tooManyFiles"}, {num: (!!props.maxConcurrentUploadRequests ? props.maxConcurrentUploadRequests : defaultMaxConcurrentUploadRequests)})
		, "error.tooManyFilesInSubmission"      : useIntl().formatMessage({id:"error.tooManyFilesInSubmission"}, {num: (!!props.maxFilesPerSubmission ? props.maxFilesPerSubmission : defaultMaxFilesPerSubmission)})
		, "error.tooManyFilesInADay"            : useIntl().formatMessage({id:"error.tooManyFilesInADay"}, {num: (!!props.maxDailyMax ? props.maxDailyMax : defaultDailyMax)})
		, "error.tooMuchDataInADay"             : useIntl().formatMessage({id:"error.tooMuchDataInADay"}, {num: bytesToNearestUnitStr(4000000000, messages)})
	}

	const [fileInputId, ] = useState("fileInputId"+Math.random());
	const [showSpinner, setShowSpinner] = useState(false);
	const [spinnerColor, setSpinnerColor] = useState("#7f0000");
	const [progress, setProgress] = useState(0);
	
	//expected inputs:
	//globalAlerts                <-- for displaying success/error messages
	//forceParentUpdate           <-- to force the parent component to re-render
	//
	//optional:
	//displayFormat               <-- one of "paperclipButton" or, default is undefined and will display a large button 
	//attachments                 <-- the array of attachments to add an attachment to, for display outside of this component
	//maxVolumePerSubmission      <-- integer, default is defaultMaxVolumePerSubmission (currently 4GB)
	//maxFilesPerSubmission       <-- integer, default is defaultMaxFilesPerSubmission (currently 500)
	//maxConcurrentUploadRequests <-- integer, default is defaultMaxConcurrentUploadRequests (currently 2)
	//maxDailyMax                 <-- integer, daily maximum number of file uploads across multiple sessions
	//maxDailyDataMax             <-- integer, daily maximum amount of data per user across multiple sessions
	//extensions                  <-- array of strings, defaults are : 
	//maxSize                     <-- integer # of bytes, default is 1GB (1,073,741,824 bytes)
	//disabled                    <-- whether or not to disable the file upload button
	//fileUploadTempId            <-- temp submissionId to use to group submission uploads into one folder for validating maxFilesPerSubmission isn't surpassed over multiple upload requests
	
	const maxSelectFile = (event) => {
		const maxFilesPerSubmission = !!props.maxFilesPerSubmission ? props.maxFilesPerSubmission : defaultMaxFilesPerSubmission	//default of maximum [defaultMaxFilesPerSubmission] concurrent file uploads
		
		let files = event.target.files // create file object
		
		if (files.length > maxFilesPerSubmission) { 
			appContext.setFlashError({duration:10000, messages:[labels["error.tooManyFilesInSubmission"]]});
			event.target.value = null // discard selected file
			
			return false;
		}
		return true;

	}

	const checkFileExtension = (event) => {
		//getting file object
		let files = event.target.files 
		
		let error = ""

		// Explore regex for combination of letters OR convert all to lowercase

		const extensions = !!props.extensions && props.extensions.length !== 0 ? props.extensions : [
			'pdf', 'mp3', 'wav', 'bmp', 'gif', 'jpeg', 'jpg', 'png', 'xls', 'xlsx', 'xlsm', 'csv', 'msg', 'ppt', 'pptx', 'pptm', 'vsd', 'doc', 'docx', 'docm', 'txt', 'xml', 'zip'
		];
		
		for (let file of files) {
			
			// compare file extension find doesn't match
			if (extensions.every(extension => file.name.toLowerCase().split(".").pop() !== extension)) {
				// create error message and assign to container   
				error += file.name;
			}
		}
		 
		
		if (error !== "") { // if message not same old that mean has error
			appContext.setFlashError({duration:10000, messages:[messages["error.followingFileTypesNotAllowed"]]});
			
			event.target.value = null;
			return false; 
		}
		return true;
	}

	const checkMultipleExtensions = (event) => {
		//getting file object
		let files = event.target.files 
		
		let error = ""
		
		for (let file of files) {
			// compare file extension find doesn't match
			if (file.name.split(".").length !== 2) {
				// create error message and assign to container   
				error = file.name;
			}
		}
		 
		
		if (error !== "") { // if message not same old that mean has error
			appContext.setFlashError({duration:10000, messages:[<FormattedMessage id="error.onlyOneFileTypeAllowed" values={{error: error, correct: error.split(".")[0]+"."+error.split(".").pop(), b: (chunks) => <b>{chunks}</b>}}/>]});
			
			event.target.value = null;
			return false; 
		}
		return true;
	}

	const checkFileSelectionSize = (event) => {
		let files = event.target.files
		const maxSize = !!props.maxSize ? props.maxSize : 1000000000  //<-- 1GigaBytes in bytes (1,000,000,000) is the default limit
		let err = "";
		let totalSize = 0;
		for(let file of files) {
			totalSize += file.size;
			if (totalSize > maxSize) {
				err += file.type;
			}
		}
		
		if (err !== '') {
			appContext.setFlashError({duration:10000, messages:[messages["error.fileTooLarge"]]});
				  
			event.target.value = null
			return false
		}

		return true;
	}

	const checkSubmissionSize = (event) => {
		let files = event.target.files
		const maxVolumePerSubmission = !!props.maxVolumePerSubmission ? props.maxVolumePerSubmission : defaultMaxVolumePerSubmission  //<-- 4Gigabytes in bytes default limit
		let volumeAlreadyUploaded = 0;
		
		//add up volumeAlreadyUploaded
		if (!!props.attachments) {
			for (let file of props.attachments) {
				if (!!file.fileSize) {
					volumeAlreadyUploaded += Number(file.fileSize);
				}
			}
		}

		const maxSize = maxVolumePerSubmission - volumeAlreadyUploaded;

		let err = "";
		let totalSize = 0;
		for(let file of files) {
			totalSize += file.size;
			if (totalSize > maxSize) {
				err += "-";
			}
		}
		
		if (err !== '') {
			const limitInGB = bytesToNearestUnitStr(maxVolumePerSubmission, messages);
			const spaceRemainingStr = bytesToNearestUnitStr(Math.max(maxSize, 0), messages);
			appContext.setFlashError({duration:10000, messages:[<FormattedMessage id="error.tooMuchDataInSubmission" values={{num: limitInGB, spaceRemaining: spaceRemainingStr}} />]});
				  
			event.target.value = null
			return false
		}

		return true;
	}

	const checkMaxConcurrentFileUploadRequests = (event) => {
		const maxConcurrentUploadRequests = !!props.maxConcurrentUploadRequests ? props.maxConcurrentUploadRequests : defaultMaxConcurrentUploadRequests
		let currentRequestCount = 0;
		let currentRequests = {}
		if (!!props.attachments) {
			for (let file of props.attachments) {
				if (file.uploadStatus !== "COMPLETE") {
					currentRequests[file.uploadedDate] = true;
				}
			}
			currentRequestCount = Object.keys(currentRequests).length;
		}

		if (currentRequestCount >= maxConcurrentUploadRequests) {
			appContext.setFlashError({duration:10000, messages:[labels["error.tooManyFiles"]]});
			event.target.value = null
			return false;
		}
		return true;
	}

	const checkFileName = (event) => {
		//getting file object
		let files = event.target.files;
		
		let error = "";

		// Explore regex for combination of letters OR convert all to lowercase

		const keyWords = !!props.keyWords && props.keyWords.length !== 0 ? props.keyWords : [
			"public", "non-confidential", "protected", "confidential", "non confidentiel", "protege", "confidentiel"
		];

		for (let file of files) {

			// compare file extension find doesn't match
			let accentlessFileName = removeAccents(file.name.toLowerCase())
			if (keyWords.every(keyWord => !accentlessFileName.includes(keyWord))) {
				// create error message and assign to container   
				if (error === '') {
					error += file.name;
				} else {
					error += ', '+file.name;
				}
			}
		}
		 
		
		if (error !== "") { // if message not same old that mean has error
			appContext.setFlashError({duration:10000, messages:[<FormattedMessage id="error.wrongFileName" values={{filename: error, b: (chunks) => <b>{chunks}</b>, english: (chunks) => <span lang="en">{chunks}</span>, french: (chunks) => <span lang="fr">{chunks}</span>}} />]});
			
			event.target.value = null;
			return false; 
		}
		return true;
	}

	const checkDuplicateFileName = (event) => {
		// get files object and loop through files in array
		for (let file of event.target.files) {
			// get new file name
			let newFileName = file.name;
			// list all the current file names
			let existingFileNames = [];
			props.attachments.map(existingFile => existingFileNames.push(existingFile.fileName));
			// throw error message if file name already exists
			if (existingFileNames.includes(newFileName)) {
				appContext.setFlashError({duration:10000, messages:[<FormattedMessage id="error.duplicateFileName" values={{filename: newFileName, b: (chunks) => <b>{chunks}</b>}} />]});
				return false; 
			}
		}

		return true;
	}

	const checkFileNameLength = (event) => {
		// get files object and loop through files in array
		for (let file of event.target.files) {
			// get file name
			let fileName = file.name;
			// check if filename length exceeds 248 characters
			if (fileName.length > 248) {
				appContext.setFlashError({duration:10000, messages:[<FormattedMessage id="error.fileNameTooLong" values={{filename: fileName, maxLength: 248, b: (chunks) => <b>{chunks}</b>}} />]});
				return false;
			}
		}
		return true;
	}
	
	async function onChangeHandler(event) {
		let files = event.target.files;
		let selectedFiles = undefined;

		if (maxSelectFile(event) && checkMaxConcurrentFileUploadRequests(event) && checkFileExtension(event) && checkMultipleExtensions(event) && checkFileSelectionSize(event) && checkSubmissionSize(event) && checkFileName(event) && checkFileNameLength(event) && checkDuplicateFileName(event)) {
			selectedFiles = files;
		}
		
		if (!!selectedFiles && selectedFiles.length > 0) {
			await onClickHandler(selectedFiles, event);
		}
	}

	const formatFileName = (fileName) => {
		const maxLength = 30;
		const extensionMatch = fileName.match(/\.[0-9a-zA-Z]+$/i);
		const extension = extensionMatch ? extensionMatch[0] : "";
		const nameWithoutExtension = fileName.replace(extension, "");

		let trimmedName = nameWithoutExtension;

		if (nameWithoutExtension.length > maxLength) {
			trimmedName = nameWithoutExtension.slice(0, maxLength) + "..."
		}
		
		return trimmedName + extension;
	}

	async function onClickHandler(selectedFiles, event) {
		const data = new FormData();
		const todaysDate = (new Date()).toISOString();
		
		if (props.displayFormat === "paperclipButton") {
			setProgress(0);
		}

		let fileNames = []
		let attachmentsList = [];

		for (let selectedFile of selectedFiles) {
			fileNames.push(formatFileName(selectedFile.name))
			
			data.append('files', selectedFile);
			attachmentsList.push({"fileName": selectedFile.name, "uploadedDate":todaysDate, "fileSize":`${selectedFile.size}`, "uploadStatus":"PENDING", "uploadProgress":0});
		}
		if (!!props.attachments) {
			props.attachments.push(...attachmentsList);
		}
		if (!!props.forceParentUpdate && typeof props.forceParentUpdate === "function") {
			if (props.displayFormat !== "paperclipButton") {
				props.forceParentUpdate();
			}
		}

		const authTokens = await getAuthTokens();
		
		let apiUrl = window.env.amplifyConfig.fileUpload_endpoint //"https://[fileUploadServer]/fileUpload";
		if (!apiUrl) {
			apiUrl = "/fileUpload"; //if empty, use relative url through cloudfront as a backup
		}
		axios.post(apiUrl, data, { // receive two parameter endpoint url ,form data 
			headers: {
				"file-count" : selectedFiles.length.toString()
				, "Authorization": authTokens.jwToken
				, "submission-id" : props.tempSubmissionId
			}
			, onUploadProgress: ProgressEvent => {
				if (!!event.target.value) {
					event.target.parentElement.reset();	//have to reset the file input field right away so that the UI will catch if a person attempts to upload a second file by the same name (otherwise the "onchange" event is never fired)
				}
				appContext.setPauseTimeout(true);
				let progress = ProgressEvent.progress;
				let factor = props.displayFormat === "paperclipButton" ? 127 : 99;	//setting max to 99 instead of 100 because there's a delay between when the file is 100% uploaded to the server and when the server is 100% finished dealing with the file.  This will make the progress stop at 99% until the server is actually 100% completed, then it'll set the progress to 100%.
				if (Number.isNaN(progress)) {
					progress = 0;
				}
				progress *= factor;

				if (props.displayFormat === "paperclipButton") {
					setShowSpinner(true);
					let green = Math.floor(progress);
					let red = 127 - green;
					setSpinnerColor(() => {return "#" + red.toString(16).padStart(2,"0") + green.toString(16).padStart(2,"0") + "00"});
				} else {
					for (let attachment of attachmentsList) {
						attachment.uploadProgress = progress;
					}
				}
				props.forceParentUpdate();
			}
		})
		.then(res => {
			appContext.setPauseTimeout(false);
			for (let attachment of attachmentsList) {
				attachment.uploadStatus = "COMPLETE";
				attachment.uploadProgress = 100;
			}
			if (props.displayFormat === "paperclipButton") {
				setShowSpinner(false);
				setSpinnerColor("#7f0000");
			}
			props.forceParentUpdate();
			appContext.setFlashNotice({duration:10000, messages:[<FormattedMessage id="home.attachments.uploadComplete" values={{fileNames: fileNames.toString(), p: (chunks) => <p>{chunks}</p>, b: (chunks) => <b>{chunks}</b>}}/>]});
			event.target.parentElement.reset();
		})
		.catch(err => {
			appContext.setPauseTimeout(false);
			if (window.env.environment !== "prod") {
				console.log({err});
			}
			for (let attachment of attachmentsList) {
				attachment.uploadStatus = "FAILED";
			}
			if (!!props.attachments) {
				for (let fileName of fileNames) {
					const badFileIndex = props.attachments.findIndex(badFile => badFile.fileName === fileName);
					if (badFileIndex !== -1) {
						props.attachments.splice(badFileIndex, 1);
					}
				}
			}
			
			if (props.displayFormat === "paperclipButton") {
				setShowSpinner(false);
				setSpinnerColor("#7f0000");
			}
			if (!!props.forceParentUpdate && typeof props.forceParentUpdate === "function") {
				if (props.displayFormat !== "paperclipButton") {
					props.forceParentUpdate();
				}
			}
			
			if (err?.response?.data?.Error?.Code === "UPLOAD_LIMIT_ERROR_VOLUME") {
				appContext.setFlashError({duration:10000, messages:[labels["error.tooMuchDataInADay"]]});
			} else if (err?.response?.data?.Error?.Code === "UPLOAD_LIMIT_ERROR_QUANTITY") {
				appContext.setFlashError({duration:10000, messages:[labels["error.tooManyFilesInSubmission"]]});
			} else if (err?.response?.data?.Error?.Code === "UPLOAD_LIMIT_ERROR") {
				appContext.setFlashError({duration:10000, messages:[labels["error.tooManyFilesInADay"]]});
			} else {
				appContext.setFlashError({duration:-1, messages:[<FormattedMessage id="http.fileUploadError" values={{fileNames: fileNames.toString(), p: (chunks) => <p>{chunks}</p>, a: (chunks) => <a href={"https://www.cbsa-asfc.gc.ca/contact/bis-sif-"+currentLongLang()+".html"} className="App-link" target="_blank" rel="noreferrer">{chunks}</a>}}/>]});
			}
			event.target.parentElement.reset();
		})
	}

	// eslint-disable-next-line
	return (
		<>
		  <form style={{visibility:"hidden", display:"none"}}><input type="file" name="file" id={fileInputId} multiple onChange={onChangeHandler} disabled={!!props.disabled} /></form>
		  {!!props.displayFormat && props.displayFormat === "paperclipButton" ?
		  	<>
		  	  <button type="button" title={messages["common.uploadAttachment"]} className="brdr-0" style={showSpinner ? {display:"none"} : {backgroundColor:"inherit"}} onClick={() => {document.getElementById(fileInputId).click()}}><span className="glyphicon glyphicon-paperclip"></span></button>
		  	  {showSpinner ? 
		  	  	<div>{Math.floor(progress * 100)}%</div>
		  	  	: <></>
		  	  }
		  	  <BarLoader
		  	    height={5}
		  	    width={27}
		  	    color={spinnerColor}
		  	    loading={showSpinner}
		  	    speedMultiplier={1}
		  	  />
		  	</>
		  : <button type="button" disabled={!!props.disabled} data-testid="uploadAttachmentBtnId" title={messages["common.uploadAttachment"]} className="btn btn-default btn-block text-center" onClick={() => {document.getElementById(fileInputId).click()}}><span className="glyphicon glyphicon-plus" style={{float:"left", fontSize:"1.2em"}}></span><FormattedMessage id="common.addAttachment"/><i> - {bytesToNearestUnitStr(props.attachments.map(attachment => parseInt(attachment.fileSize)).reduce((accumulator, currentSize) => accumulator + currentSize, 0), messages, 2)} {}<FormattedMessage id="ace.upload.file-number" values={{number: props.attachments.length, s: props.attachments.length === 1 ? '' : 's'}}/></i></button>
		  }
		  
		</>
	);
}

const getAuthTokens = async () => {
	const jwToken = "Bearer " + (await Auth.currentSession()).getAccessToken().getJwtToken();
	return {
		jwToken: jwToken
	};
}